import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Mail, Phone, Gift, Users, Star, BarChart3, MessageSquare, Check, ArrowRight, TrendingUp, Bell, Home, Heart, Shield } from 'lucide-react';

const services = [
  {
    icon: Mail,
    title: "Post-Close Outreach",
    subtitle: "Email & Call Cadence",
    description: "We reach out to your clients on your behalf with a professionally crafted email and phone sequence designed to maintain your brand presence.",
    features: [
      "Personalized introduction email within 24 hours",
      "Follow-up phone call within 48-72 hours",
      "Congratulatory messaging branded to you",
      "30-day follow-up sequence",
      "All communications feel like they're from your team"
    ],
    highlight: true
  },
  {
    icon: Star,
    title: "Review Generation",
    subtitle: "Platform-Agnostic Collection",
    description: "Systematic collection of 5-star reviews designed to boost your online reputation across all major platforms.",
    features: [
      "Google Business Profile reviews",
      "Zillow & Realtor.com reviews",
      "Facebook recommendations",
      "Custom platform support available",
      "Optimal timing for maximum response"
    ],
    highlight: false
  },
  {
    icon: Gift,
    title: "Gift Coordination",
    subtitle: "Selection & Fulfillment",
    description: "Delight your clients with thoughtful, personalized gifts they'll actually appreciate. We handle selection, ordering, and delivery.",
    features: [
      "Curated gift options for client selection",
      "Personalized gift notes",
      "White-glove delivery coordination",
      "Budget-friendly options available",
      "Brand-aligned presentation"
    ],
    highlight: false
  },
  {
    icon: Users,
    title: "Referral Capture",
    subtitle: "Collection & Routing",
    description: "We professionally request referrals from satisfied clients and route them directly back to you with all relevant information.",
    features: [
      "Natural, non-pushy referral requests",
      "Warm lead capture with context",
      "Direct routing to your inbox",
      "Follow-up for maximum conversion",
      "Monthly referral reporting"
    ],
    highlight: true
  }
];

const clientBenefits = [
  {
    icon: TrendingUp,
    title: "Lifetime Loan Monitoring",
    description: "Clients are automatically enrolled in our lifetime loan tracking system. We monitor rates and market conditions, proactively reaching out when opportunities to save money arise."
  },
  {
    icon: Gift,
    title: "Closing Gift Program",
    description: "Every client receives a personalized closing gift of their choice—a thoughtful way to celebrate their new home and create a lasting positive impression."
  },
  {
    icon: Bell,
    title: "Rate Watch Alerts",
    description: "Automatic notifications when refinancing could benefit them, ensuring clients never miss an opportunity to reduce their monthly payments."
  },
  {
    icon: Home,
    title: "Home Value Tracking",
    description: "Periodic updates on their home's estimated value help clients stay informed about their equity position and biggest investment."
  },
  {
    icon: Shield,
    title: "Dedicated Support",
    description: "A personal concierge handles any post-closing questions, providing ongoing support long after the transaction closes."
  },
  {
    icon: Heart,
    title: "VIP Referral Network",
    description: "Clients who refer others join our VIP network, unlocking exclusive perks and priority service for their future mortgage needs."
  }
];

const additionalServices = [
  {
    title: "Client Anniversary Outreach",
    description: "Annual check-ins on home purchase anniversaries",
    status: "Coming Soon"
  },
  {
    title: "Holiday Greetings",
    description: "Seasonal cards and messages to stay top of mind",
    status: "Coming Soon"
  },
  {
    title: "Market Update Sharing",
    description: "Automated market updates to past clients",
    status: "Coming Soon"
  }
];

export const ServicesPage = () => {
  return (
    <div data-testid="services-page" className="min-h-screen bg-[#0F0F0F]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="h-[2px] w-16 bg-[#C9A227] mx-auto mb-8" />
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">What We Offer</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Our <span className="text-[#E5D5A5]">Services</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              Comprehensive post-closing concierge services designed to elevate your client 
              experience and generate more reviews, retention, and referrals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid="services-grid" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative overflow-hidden ${
                  service.highlight 
                    ? 'bg-gradient-to-b from-[#1A1A1A] to-[#0F0F0F] border border-[#C9A227]/30'
                    : 'bg-[#1A1A1A] border border-white/5'
                } p-8 hover:border-[#C9A227]/50 transition-all duration-500`}
              >
                {service.highlight && (
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A227]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                )}
                
                <div className="flex items-start gap-4 mb-6">
                  <div className="bg-[#C9A227]/10 p-3 flex-shrink-0">
                    <service.icon className="w-8 h-8 text-[#C9A227]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[#C9A227] text-xs tracking-wider uppercase mb-1">
                      {service.subtitle}
                    </p>
                    <h3 className="font-serif text-2xl text-white">{service.title}</h3>
                  </div>
                </div>
                
                <p className="text-neutral-400 leading-relaxed mb-6">
                  {service.description}
                </p>
                
                <ul className="space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start text-sm text-neutral-300">
                      <Check className="w-4 h-4 text-[#C9A227] mr-3 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Benefits Section */}
      <section data-testid="client-benefits-section" className="py-24 bg-[#141414] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Client Experience</p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
              What Your <span className="text-[#E5D5A5]">Clients Receive</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
              Our service isn't just for brokers—your clients get real, lasting value that 
              makes their investment worthwhile and keeps them coming back.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`client-benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#1A1A1A] border border-white/5 p-6 group hover:border-[#C9A227]/30 transition-all duration-500"
              >
                <benefit.icon className="w-10 h-10 text-[#C9A227] mb-4" strokeWidth={1.5} />
                <h3 className="font-serif text-lg text-white mb-2">{benefit.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-24 bg-[#141414] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
          >
            <div>
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">The Process</p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
                Seamless Integration with <span className="text-[#E5D5A5]">Your Workflow</span>
              </h2>
              <p className="text-neutral-300 text-lg leading-relaxed mb-8">
                Our services are designed to work alongside your existing processes. 
                Simply hand off your closed loans, and we take care of everything else.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: MessageSquare, text: "No changes to your current systems" },
                  { icon: BarChart3, text: "Monthly reporting on all activities" },
                  { icon: Users, text: "Dedicated account support" }
                ].map((item) => (
                  <div key={item.text} className="flex items-center text-neutral-300">
                    <item.icon className="w-5 h-5 text-[#C9A227] mr-4" strokeWidth={1.5} />
                    {item.text}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-[#C9A227]/5 blur-3xl" />
              <img
                src="https://images.pexels.com/photos/8036584/pexels-photo-8036584.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Concierge service"
                className="relative w-full h-[400px] object-cover border border-white/10"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coming Soon Services */}
      <section data-testid="coming-soon-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Expanding</p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white">
              Optional <span className="text-[#E5D5A5]">Add-Ons</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#1A1A1A] border border-white/5 p-6 relative overflow-hidden group"
              >
                <div className="absolute top-4 right-4">
                  <span className="text-[#C9A227] text-xs tracking-wider uppercase bg-[#C9A227]/10 px-3 py-1">
                    {service.status}
                  </span>
                </div>
                <h3 className="font-serif text-xl text-white mb-2 pr-24">{service.title}</h3>
                <p className="text-neutral-500 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#141414] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to Elevate Your <span className="text-[#E5D5A5]">Client Experience?</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              Let us show you how Better Closings can transform your post-close 
              workflow and help you generate more reviews and referrals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="services-cta-primary">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-10 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  Book a Demo
                </Button>
              </Link>
              <Link to="/how-it-works" data-testid="services-cta-secondary">
                <Button variant="outline" className="bg-transparent border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 rounded-none px-10 py-6 font-semibold text-sm tracking-wider uppercase transition-all duration-300 group">
                  See How It Works
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
